import { MantineColor, MantineSpacing } from '@mantine/core';
import { MutableRefObject } from 'react';
import { ChatMode, ChatState, FinalChunkData } from './chat/types';
import { ThinkingEffort } from './chats/thinkingEffort';

export type ContentPaneConfig = {
  bg?: MantineColor;
  p?: MantineSpacing;
};

export type ContentMeta = {
  pageType: string;
  entityId?: string;
  metadata?: Record<string, string>;
  chatMode?: ChatMode;
  onChatStateChange?: (state: ChatState, data?: FinalChunkData) => void;
  getDocumentContent?: () => string;
  chatActionsRef?: MutableRefObject<ChatActions | null>;
  // The cashflow model on screen, sent with each message so Siesta knows which
  // model "this model" means.
  cashflowScenarioId?: string;
};

export type ChatActions = {
  replaceLastAssistantMessage: (content: string) => void;
  sendVerifyMessage: (claim: string, sectionPath: string[]) => void;
};

export type SiestaMode = 'action' | 'chat' | 'busy' | 'history';

export const SiestaActionType = {
  LINK: 'link',
  CHAT: 'chat',
} as const;

export type SiestaActionType = (typeof SiestaActionType)[keyof typeof SiestaActionType];

export type DescriptionMode = 'hover' | 'always_visible';

export type SiestaAction =
  | {
      type: typeof SiestaActionType.LINK;
      id: string;
      label: string;
      description?: string;
      descriptionMode?: DescriptionMode;
      href: string;
      linkState?: { familyId?: string };
    }
  | {
      type: typeof SiestaActionType.CHAT;
      id: string;
      label: string;
      description?: string;
      descriptionMode?: DescriptionMode;
      prompt?: string;
    };

export type AppContainerContextValue = {
  skipDefaultSidebar: boolean;
  setSkipDefaultSidebar: (skip: boolean) => void;
  sidebarContent: React.ReactNode | null;
  setSidebarContent: (content: React.ReactNode | null) => void;
  contentMeta: ContentMeta | null;
  setContentMeta: (meta: ContentMeta | null) => void;
  contentPaneConfig: ContentPaneConfig;
  setContentPaneConfig: (config: ContentPaneConfig) => void;
  siestaMode: SiestaMode;
  setSiestaMode: (mode: SiestaMode) => void;
  siestaBusyGlowTop: number | null;
  setSiestaBusyGlowTop: (top: number | null) => void;
  conversationId: string;
  setConversationId: (id: string) => void;
  resetConversationId: () => string;
  // The thinking effort the adviser chose in a conversation, which then stays for the rest of it.
  // A conversation they have not touched has no entry and is answered at the default. Kept here,
  // beside the conversation id, because the sidebar is torn down whenever a page supplies its own.
  chosenThinkingEfforts: Record<string, ThinkingEffort>;
  chooseThinkingEffort: (conversationId: string, effort: ThinkingEffort) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  preventSidebarCollapse: boolean;
  setPreventSidebarCollapse: (prevent: boolean) => void;
  sidebarFullScreen: boolean;
  setSidebarFullScreen: (fullScreen: boolean) => void;
};
